import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, Search, SlidersHorizontal, Grid, List, ChevronDown } from 'lucide-react';
import api from '../utils/api';
import ListingCard from '../components/listings/ListingCard';

const CITIES = ['دبي', 'أبوظبي', 'الشارقة', 'عجمان', 'الرياض', 'جدة', 'القاهرة'];
const SORT_OPTIONS = [
  { value: 'created_at-DESC', label: 'الأحدث' },
  { value: 'price_per_hour-ASC', label: 'السعر: الأقل' },
  { value: 'price_per_hour-DESC', label: 'السعر: الأعلى' },
  { value: 'rating_avg-DESC', label: 'الأعلى تقييماً' },
];

export default function ListingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    type: searchParams.get('type') || '',
    category: searchParams.get('category') || '',
    city: searchParams.get('city') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    sort: 'created_at',
    order: 'DESC',
    page: 1
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchListings();
  }, [filters]);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/categories');
      setCategories(res.data.data || []);
    } catch {}
  };

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const res = await api.get(`/listings?${params.toString()}`);
      setListings(res.data.data || []);
      setPagination(res.data.pagination || {});
    } catch {
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const typeLabel = { venue: 'الملاعب والمنشآت', equipment: 'المعدات والأجهزة', service: 'الخدمات الرياضية' };

  return (
    <div className="min-h-screen pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="py-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            {filters.type ? typeLabel[filters.type] : 'جميع الإعلانات'}
          </h1>
          <p className="text-dark-400 text-sm">{pagination.total || 0} نتيجة متاحة</p>
        </div>

        <div className="flex gap-6">
          {/* Sidebar Filters - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="bg-dark-800 border border-dark-700 rounded-2xl p-5 sticky top-24 space-y-6">
              <h3 className="text-white font-bold flex items-center gap-2">
                <SlidersHorizontal size={16} className="text-primary-400" /> الفلاتر
              </h3>

              {/* Type */}
              <div>
                <label className="text-dark-300 text-xs font-medium mb-2 block">النوع</label>
                <div className="space-y-1.5">
                  {[
                    { value: '', label: 'الكل' },
                    { value: 'venue', label: 'ملاعب ومنشآت' },
                    { value: 'equipment', label: 'معدات وأجهزة' },
                    { value: 'service', label: 'خدمات رياضية' },
                  ].map(({ value, label }) => (
                    <label key={value} className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="type" value={value} checked={filters.type === value}
                        onChange={() => updateFilter('type', value)}
                        className="accent-primary-500" />
                      <span className="text-dark-200 text-sm">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* City */}
              <div>
                <label className="text-dark-300 text-xs font-medium mb-2 block">المدينة</label>
                <select value={filters.city} onChange={e => updateFilter('city', e.target.value)}
                  className="form-input text-sm py-2">
                  <option value="">جميع المدن</option>
                  {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Price Range */}
              <div>
                <label className="text-dark-300 text-xs font-medium mb-2 block">نطاق السعر (درهم/ساعة)</label>
                <div className="flex gap-2">
                  <input type="number" placeholder="من" value={filters.min_price}
                    onChange={e => updateFilter('min_price', e.target.value)}
                    className="form-input text-sm py-2 w-1/2" />
                  <input type="number" placeholder="إلى" value={filters.max_price}
                    onChange={e => updateFilter('max_price', e.target.value)}
                    className="form-input text-sm py-2 w-1/2" />
                </div>
              </div>

              {/* Categories */}
              {filters.type && categories.filter(c => c.type === filters.type).length > 0 && (
                <div>
                  <label className="text-dark-300 text-xs font-medium mb-2 block">الفئة</label>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="category" value="" checked={!filters.category}
                        onChange={() => updateFilter('category', '')} className="accent-primary-500" />
                      <span className="text-dark-200 text-sm">الكل</span>
                    </label>
                    {categories.filter(c => c.type === filters.type).map(cat => (
                      <label key={cat.id} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="category" value={cat.slug} checked={filters.category === cat.slug}
                          onChange={() => updateFilter('category', cat.slug)} className="accent-primary-500" />
                        <span className="text-dark-200 text-sm">{cat.name_ar}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={() => setFilters({ type: '', category: '', city: '', min_price: '', max_price: '', sort: 'created_at', order: 'DESC', page: 1 })}
                className="w-full text-dark-400 hover:text-red-400 text-sm transition-colors text-center">
                مسح الفلاتر
              </button>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-4 gap-3">
              <button onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden flex items-center gap-2 bg-dark-700 text-dark-200 px-4 py-2 rounded-xl text-sm">
                <Filter size={14} /> فلترة
              </button>

              <select value={`${filters.sort}-${filters.order}`}
                onChange={e => {
                  const [sort, order] = e.target.value.split('-');
                  setFilters(prev => ({ ...prev, sort, order }));
                }}
                className="bg-dark-700 border border-dark-600 text-white text-sm px-3 py-2 rounded-xl outline-none mr-auto">
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            {/* Listings Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {[...Array(9)].map((_, i) => (
                  <div key={i} className="bg-dark-800 rounded-2xl overflow-hidden">
                    <div className="skeleton h-48" />
                    <div className="p-4 space-y-2">
                      <div className="skeleton h-4 rounded" />
                      <div className="skeleton h-3 rounded w-2/3" />
                      <div className="skeleton h-3 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : listings.length === 0 ? (
              <div className="text-center py-20 bg-dark-800 rounded-2xl border border-dark-700">
                <Search size={48} className="mx-auto mb-4 text-dark-600" />
                <h3 className="text-white font-bold text-xl mb-2">لا توجد نتائج</h3>
                <p className="text-dark-400">جرب تغيير الفلاتر للحصول على نتائج أخرى</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {listings.map(listing => <ListingCard key={listing.id} listing={listing} />)}
              </div>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                {[...Array(pagination.pages)].map((_, i) => (
                  <button key={i} onClick={() => updateFilter('page', i + 1)}
                    className={`w-9 h-9 rounded-xl text-sm font-medium transition-colors ${
                      filters.page === i + 1
                        ? 'bg-primary-600 text-white'
                        : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
                    }`}>
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
